// Single source of truth for the backend TaxCCSpecialPaymentTypeEnum
// (tmc-local-backend/src/graphql/taxProgram/taxEnums.js -> taxCCSpecialPaymentTypeEnum).
//
// specialPaymentType is a STRICT GraphQL enum on the upsertTaxCCPayment input
// (ccPaymentsInputType). GraphQL coerces enum *variables by their NAME* (VISA,
// MASTERCARD, ...), NOT by their internal value ('visa', 'mc', ...). Sending the
// internal value -- or '' / null / any non-enum label -- throws an HTTP 500 at
// variable coercion, before the resolver runs ("got invalid value \"visa\" ...
// Did you mean VISA?"). The only save-safe states are a valid enum NAME or an
// ABSENT field. The read path already returns NAMES (enum output serialization),
// so emitting NAMES on save keeps both directions consistent.
//
// NAME -> internal value, mirroring the backend enum exactly. Kept in sync manually
// since the two repos do not share a constant.
export const CC_SPECIAL_TYPE_NAME_TO_VALUE = {
  // Forte
  ECHECK: 'echeck',
  VISA: 'visa',
  MASTERCARD: 'mc',
  AMERICANEXPRESS: 'amex',
  DISCOVER: 'disc',
  WEB: 'web',
  COUNTER: 'counter',
  IVR: 'ivr',
  OTHER: 'other',
  // Payrix
  ECHECK_PAYRIX: 'echeck_payrix',
  VISA_PAYRIX: 'visa_payrix',
  MC_PAYRIX: 'mc_payrix',
  AMEX_PAYRIX: 'amex_payrix',
  DISCOVER_PAYRIX: 'discover_payrix',
  WEB_PAYRIX: 'web_payrix',
  COUNTER_PAYRIX: 'counter_payrix',
  IVR_PAYRIX: 'ivr_payrix',
  OTHER_PAYRIX: 'other_payrix',
  // Forte2
  ECHECK_FORTE2: 'echeck_forte2',
  VISA_FORTE2: 'visa_forte2',
  MASTERCARD_FORTE2: 'mc_forte2',
  AMERICANEXPRESS_FORTE2: 'amex_forte2',
  DISCOVER_FORTE2: 'disc_forte2',
  WEB_FORTE2: 'web_forte2',
  COUNTER_FORTE2: 'counter_forte2',
  IVR_FORTE2: 'ivr_forte2',
  OTHER_FORTE2: 'other_forte2',
};

// The set of wire-valid enum NAMES (what upsertTaxCCPayment actually accepts).
export const VALID_CC_SPECIAL_TYPES = new Set(Object.keys(CC_SPECIAL_TYPE_NAME_TO_VALUE));

// Case-insensitive lookup keyed by BOTH the enum name AND the internal value,
// every key lowercased, resolving to the canonical enum NAME. This accepts input
// from any source -- the read path (already a NAME like 'VISA'), the credit-type
// picker (an internal value like 'visa' / 'mc'), or mixed casing/whitespace.
const NAME_LOOKUP = {};
Object.entries(CC_SPECIAL_TYPE_NAME_TO_VALUE).forEach(([name, value]) => {
  NAME_LOOKUP[name.toLowerCase()] = name; // 'visa' -> 'VISA', 'mc_payrix' -> 'MC_PAYRIX'
  NAME_LOOKUP[value.toLowerCase()] = name; // 'mc' -> 'MASTERCARD', 'disc' -> 'DISCOVER'
});

// Returns the wire-valid enum NAME for the given input, or null.
// Blank is expected (Payrix hides the card-type selector) and stays silent; a
// non-empty unrecognized brand is logged for diagnosis but still dropped to null.
export const normalizeSpecialPaymentType = (creditType) => {
  const key = String(creditType || '').trim().toLowerCase();
  if (NAME_LOOKUP[key]) return NAME_LOOKUP[key];
  if (key) console.warn('[CC] dropping unrecognized specialPaymentType:', key);
  return null;
};

// Chokepoint guard: returns a shallow copy of a taxCCPayments upsert item that can
// never 500 on specialPaymentType -- the field is normalized to a valid enum NAME,
// or removed entirely. Every CC save path funnels through upsertCCPaymentsTrans, so
// sanitizing here protects keyed, charged-Forte, charged-Payrix, the CC edit/bulk
// screen, and any future caller. The caller's object is not mutated.
export const sanitizeCCPaymentItem = (item) => {
  if (!item || typeof item !== 'object') return item;
  const safe = { ...item };
  const normalized = normalizeSpecialPaymentType(safe.specialPaymentType);
  if (normalized) safe.specialPaymentType = normalized;
  else delete safe.specialPaymentType;
  return safe;
};

export default {
  CC_SPECIAL_TYPE_NAME_TO_VALUE,
  VALID_CC_SPECIAL_TYPES,
  normalizeSpecialPaymentType,
  sanitizeCCPaymentItem,
};
